/**
 * Formato da saida do admin para as outras aplicacoes da plataforma.
 *
 * O admin e onde a pessoa se autentica; as demais aplicacoes recebem a sessao
 * por aqui. Como `localStorage` e por origem, a sessao nao atravessa sozinha.
 *
 * Existem dois formatos, e este arquivo e o unico lugar que os conhece:
 *
 *  1. **ticket** (`/sso#k=<ticket>`) -- o atual. O admin pede o ticket ao
 *     `liberty/auth`, que devolve tambem o destino ja resolvido no registry. O
 *     ticket e opaco, vale segundos e queima no primeiro uso; quem o troca
 *     recebe um token proprio, com `aud` da aplicacao de destino.
 *  2. **token no fragmento** (`/sso#t=<jwt>&c=<corp>`) -- o formato anterior,
 *     mantido so enquanto houver ambiente com a API de auth sem
 *     `POST /sso/ticket`. Manda o token de sessao inteiro, com a validade
 *     inteira: e o que o ticket veio resolver.
 *
 * Tudo aqui e funcao pura -- a chamada HTTP e a navegacao ficam em
 * `services/api/apps.js`, para que estas regras deem para testar com
 * `node --test`.
 */

/** Link no formato atual. O destino interno ja foi enviado ao pedir o ticket. */
/**
 * Lê o ticket de um handoff que chegou no fragmento (`/sso#k=<ticket>`).
 *
 * Só o formato de ticket: o admin nunca precisou aceitar o formato anterior
 * (token inteiro no fragmento) porque nunca recebeu handoff nenhum até a
 * personificação existir. Devolve `null` quando não há ticket na URL.
 */
export function lerHandoffDeTicket() {
    const fragmento = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const ticket = fragmento.get('k');

    return ticket || null;
}

/**
 * Tira o handoff da barra de endereços assim que foi lido. Sem isto o ticket
 * fica no histórico do navegador e em qualquer print de tela -- mesmo de uso
 * único, não deveria continuar visível ali.
 */
export function limparFragmento() {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
}

export function montarLinkDeTicket({ redirectUrl, ticket } = {}) {
    if (!redirectUrl || !ticket) {
        return null;
    }

    return `${String(redirectUrl).replace(/\/+$/, '')}#k=${encodeURIComponent(ticket)}`;
}

/** Link no formato anterior, com o token da sessao no fragmento. */
export function montarLinkDeSso({ url, token, corp, destino } = {}) {
    if (!url || !token) {
        return null;
    }

    const base = String(url).replace(/\/+$/, '');
    const fragmento = new URLSearchParams({ t: token });

    if (corp) {
        fragmento.set('c', corp);
    }

    const interno = caminhoInterno(destino);
    if (interno) {
        fragmento.set('r', interno);
    }

    return `${base}/sso#${fragmento.toString()}`;
}

/**
 * Se vale cair no formato anterior quando o pedido de ticket falha.
 *
 * Só quando o servidor **não sabe** emitir ticket (rota inexistente, fora do
 * ar, sem resposta). Recusa explicita -- 400, 401, 403, 404 com corpo de erro
 * -- e decisao do servidor sobre esta pessoa e esta aplicacao, e passar por
 * cima dela mandando o token seria contornar a checagem de permissao.
 */
export function deveTentarFormatoAntigo(erro) {
    const status = erro?.status ?? erro?.response?.status;

    if (!status) {
        return true;
    }

    if (status >= 500) {
        return true;
    }

    if (status === 404) {
        // 404 do registry vem com `error`; 404 de rota inexistente, nao.
        return !erro?.response?.data?.error;
    }

    return false;
}

/** So caminho interno: destino absoluto vindo de fora seria open redirect. */
function caminhoInterno(valor) {
    if (!valor || !valor.startsWith('/') || valor.startsWith('//')) {
        return null;
    }

    return valor;
}

/**
 * Se a resposta diz que a sessao foi encerrada em outro lugar.
 *
 * O logout derruba a sessao inteira (claim `sid`), em todas as aplicacoes. Sem
 * reconhecer isso, esta aba so descobriria no proximo clique -- e mostraria erro
 * generico em vez de voltar para o login.
 *
 * A checagem e estreita de proposito: 401 generico continua sendo tratado como
 * antes (o guard tenta renovar o token), senao um token vencido viraria
 * logout em vez de renovacao.
 */
export function sessaoFoiRevogada(erro) {
    const status = erro?.status ?? erro?.response?.status;

    return status === 401 && erro?.response?.data?.error === 'Session revoked.';
}

/**
 * Pedido de volta trazido por uma aplicacao que mandou o usuario se autenticar.
 *
 * Quando alguem abre o accounts sem sessao, o accounts nao tem tela de login:
 * manda a pessoa para ca com `?app=<slug>&r=<caminho>`. Terminado o login, o
 * admin emite um ticket para aquela aplicacao e devolve a pessoa ao lugar de
 * onde ela saiu -- que e a diferenca entre "entrei e estou onde queria" e
 * "entrei e agora acho o caminho de novo".
 *
 * O que chega e so um slug e um caminho, nunca uma URL. A origem de destino
 * sai do registry no servidor, na emissao do ticket, e a permissao e conferida
 * la tambem: um slug inventado nao abre nada que a pessoa ja nao pudesse abrir.
 * Por isso este parse pode ser ingenuo -- ele nao e a checagem.
 */
export function lerPedidoDeVolta(search = '') {
    const parametros = new URLSearchParams(String(search).replace(/^\?/, ''));
    const slug = (parametros.get('app') || '').trim().toLowerCase();

    if (!slug) {
        return null;
    }

    return { slug, destino: caminhoInterno(parametros.get('r')) };
}

/**
 * Como esta aplicacao se identifica para o auth no login.
 *
 * O slug e o mesmo cadastrado no registry (`app.slug`), e e ele que faz o token
 * sair com `aud` e com a lista de APIs que o admin pode chamar. Sem isso o
 * token sai sem audiencia -- que e o comportamento anterior, aceito enquanto as
 * APIs nao exigirem a lista.
 */
export function slugDaAplicacao(config = {}) {
    const slug = config.APP_SLUG || config.APP_NAME || '';

    return String(slug).trim().toLowerCase() || null;
}
